import Avatar from '@/components/common/Avatar';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import Flex from '@/components/common/Flex';
import { darkenColor, getColor, hexToRgb } from '@/helpers/utils';
import { successMessage } from '@/utils/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { DateObject } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persianLocale from 'react-date-object/locales/persian_fa';
import { useFormikContext } from 'formik';
import type { Dispatch, SetStateAction } from 'react';

interface KeywordNote {
  id: number | string;
  description: string;
  created_at: string;
  sender?: { name?: string; image?: string };
  is_public: boolean;
  is_owner: boolean;
  parent?: { id: number | string; description: string };
}

interface EditingNote {
  id: number | string;
  description: string;
  is_public?: boolean;
  sender?: { name?: string; image?: string };
}

export type { EditingNote };

interface NoteItemsProps {
  notes: KeywordNote[];
  edit: EditingNote | null;
  setEdit: Dispatch<SetStateAction<EditingNote | null>>;
  isLoading: boolean;
  replyingMessage: EditingNote | null;
  setReplyingMessage: Dispatch<SetStateAction<EditingNote | null>>;
  updateNote: (args: {
    id: number | string;
    description: string;
    is_public: boolean;
  }) => { then: (cb: () => void) => void };
  setDeletingNote: Dispatch<SetStateAction<EditingNote | null>>;
}

const NoteItems = ({
  notes,
  edit,
  setEdit,
  isLoading,
  replyingMessage,
  setReplyingMessage,
  updateNote,
  setDeletingNote
}: NoteItemsProps) => {
  const [actions] = useState([
    // {
    //   tooltip: 'Forward',
    //   icon: 'share'
    // },
    {
      tooltip: 'پاسخ',
      icon: 'reply'
    },
    {
      tooltip: 'ویرایش',
      icon: 'edit'
    },
    {
      tooltip: 'حذف',
      icon: 'trash-alt'
    }
  ]);
  const { setFieldValue } = useFormikContext();
  return (
    <div className="chat-content-scroll-area mt-3">
      {isLoading ? (
        <div className="text-center">
          <Spinner
            className="me-1"
            style={{
              width: '16px',
              height: '16px'
            }}
          />
          درحال دریافت یادداشت ها
        </div>
      ) : notes?.length > 0 ? (
        notes?.map(
          ({
            created_at,
            id,
            description,
            sender,
            is_public,
            is_owner,
            parent
          }) => {
            const utcDate = new Date(created_at);

            const formattedTime =
              utcDate.toISOString().substring(11, 16) +
              ' ' +
              new DateObject({
                date: utcDate,
                calendar: persian,
                locale: persianLocale
              }).format('YYYY/MM/DD');

            return (
              <div id={id} key={id}>
                <Flex className={classNames('p-1 pb-3 mx-2')}>
                  <div
                    className={classNames('flex-1 mx-2', {
                      'd-flex justify-content-end': !is_owner
                    })}
                    style={{
                      maxWidth: '100%',
                      marginTop: '-2px'
                    }}
                  >
                    <div
                      className={classNames('')}
                      style={{
                        width: '95%',
                        maxWidth: '1200px',
                        minWidth: '400px'
                      }}
                    >
                      <Flex
                        alignItems="center"
                        className={classNames('hover-actions-trigger', {
                          'flex-end-center': !is_owner,
                          'align-items-center': is_owner
                        })}
                      >
                        <>
                          {!is_owner && (
                            <ul className="hover-actions position-relative list-inline mb-0 text-400 mx-2">
                              {!replyingMessage &&
                                !edit &&
                                actions
                                  .filter(
                                    action =>
                                      is_owner || action.icon === 'reply'
                                  )
                                  .map(action => (
                                    <li
                                      key={action.tooltip}
                                      className="list-inline-item cursor-pointer chat-option-hover"
                                      onClick={() => {
                                        setReplyingMessage({
                                          id,
                                          description,
                                          sender
                                        });
                                      }}
                                    >
                                      <ChildrensTooltip
                                        placement="top"
                                        html={action.tooltip}
                                        delayToRemove={0}
                                      >
                                        <div>
                                          <FontAwesomeIcon
                                            icon={action.icon}
                                            className="d-inline-block"
                                            width={12}
                                          />
                                        </div>
                                      </ChildrensTooltip>
                                    </li>
                                  ))}
                            </ul>
                          )}

                          <div
                            className={classNames(
                              'p-1 rounded-2 chat-message',
                              {
                                'bg-200': !is_owner,
                                'bg-primary': is_owner
                              }
                            )}
                            style={
                              is_owner
                                ? document.documentElement.getAttribute(
                                    'data-theme-variant'
                                  ) === 'primary' ||
                                  document.documentElement.getAttribute(
                                    'data-theme-variant'
                                  ) === 'yellow'
                                  ? {
                                      color: 'black'
                                    }
                                  : {
                                      color: 'white'
                                    }
                                : {}
                            }
                          >
                            {parent && (
                              <div
                                onClick={() => {
                                  const target = document.getElementById(
                                    parent.id
                                  );
                                  if (target) {
                                    target.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center'
                                    });
                                    target.classList.add('highlight-animation');
                                    setTimeout(() => {
                                      target.classList.remove(
                                        'highlight-animation'
                                      );
                                    }, 2000);
                                  }
                                }}
                                style={{
                                  backgroundColor: is_owner
                                    ? `rgb(${darkenColor(hexToRgb(getColor('primary')), 40)})`
                                    : `rgb(${darkenColor(hexToRgb(getColor('gray-200')), 40)})`
                                }}
                                className="rounded-2 cursor-pointer fs--2 mb-1 border-primary border-start ps-1"
                              >
                                <p
                                  className="mb-0 p-0 text-break text-truncate-multiline"
                                  dangerouslySetInnerHTML={{
                                    __html: parent.description
                                  }}
                                />
                              </div>
                            )}
                            {description && (
                              <p
                                className="mb-0 fs--1 p-1 text-break"
                                dangerouslySetInnerHTML={{
                                  __html: description
                                }}
                              />
                            )}
                          </div>
                        </>
                        {is_owner && (
                          <ul className="hover-actions position-relative list-inline mb-0 text-400 mx-2">
                            {!replyingMessage &&
                              !edit &&
                              actions.map(action => (
                                <li
                                  key={action.tooltip}
                                  className="list-inline-item cursor-pointer chat-option-hover"
                                  onClick={() => {
                                    if (action.icon === 'edit') {
                                      setEdit({
                                        id,
                                        description,
                                        is_public
                                      });
                                      setFieldValue('note', description);
                                    } else if (action.icon === 'trash-alt') {
                                      setDeletingNote({
                                        id,
                                        description
                                      });
                                    } else if (action.icon === 'reply') {
                                      setReplyingMessage({
                                        id,
                                        description,
                                        sender
                                      });
                                    }
                                  }}
                                >
                                  <ChildrensTooltip
                                    placement="top"
                                    html={action.tooltip}
                                    delayToRemove={0}
                                  >
                                    <div>
                                      <FontAwesomeIcon
                                        icon={action.icon}
                                        className="d-inline-block"
                                        width={12}
                                      />
                                    </div>
                                  </ChildrensTooltip>
                                </li>
                              ))}
                          </ul>
                        )}
                      </Flex>
                      <Flex
                        className={classNames('hover-actions-trigger', {
                          'flex-end-center': !is_owner,
                          'align-items-center': is_owner
                        })}
                      >
                        <div
                          className={classNames(
                            'text-400 gap-1 d-flex gap-0 fs--2'
                            // {
                            //   'text-start':
                            //     !isNotYourMessage && isRTL
                            // },
                            // {
                            //   'text-end':
                            //     !isNotYourMessage && !isRTL
                            // }
                          )}
                        >
                          {is_owner && (
                            <Form.Switch
                              onClick={() => {
                                updateNote({
                                  id,
                                  description,
                                  is_public: !is_public
                                }).then(() => {
                                  successMessage(
                                    !is_public
                                      ? 'یادداشت شما عمومی شد'
                                      : 'یادداشت شما خصوصی شد'
                                  );
                                });
                              }}
                              checked={!is_public}
                              className="m-0"
                            />
                          )}

                          <span className="font-weight-semi-bold">
                            {sender?.name}
                          </span>
                          <span>-</span>
                          {formattedTime}
                        </div>
                      </Flex>
                    </div>
                  </div>
                  {!is_owner && <Avatar src={sender?.image} />}
                </Flex>
              </div>
            );
          }
        )
      ) : (
        <div className="text-center pt-3">
          {/* یادداشتی برای نمایش وجود ندارد. */}
        </div>
      )}
    </div>
  );
};

export default NoteItems;
